import { MathUtils, Vector3 } from "three";
import {
  PipeSelectionMask,
  type PipeMaskSnapshot,
} from "./pipe-selection-mask";
import type { PipeTraceResult, PipeTraceUpdate } from "./pipe-tracker";
import { traceLodPipeRun } from "./trace-lod-pipe";
import type { SurfaceSelection } from "./surface-selection-pass";
export type PipeRunSeed = Pick<
  SurfaceSelection,
  "cloudId" | "cameraId" | "origin" | "cylinder"
>;

export type PipeRunSelection = {
  kind: "pipe-run";
  id: string;
  cloudId: string;
  cameraId: string;
  origin: Vector3;
  mask: PipeMaskSnapshot;
  centerline: Float32Array;
  inferredCenterline: Float32Array;
  length: number;
  inferredLength: number;
  coverage: "growing" | "partial";
  result?: PipeTraceResult;
};

/** Assembles immutable pipe-run revisions without borrowing an analytic cylinder mask. */
export class PipeRunSelectionBuilder {
  private readonly id = MathUtils.generateUUID();
  private readonly mask: PipeSelectionMask;
  private readonly observed: number[] = [];
  private readonly inferred: number[] = [];
  private length = 0;
  private inferredLength = 0;
  private readonly origin: Vector3;

  /**
   * @param source Stable source/camera identity.
   * @param origin Local display origin.
   */
  constructor(
    private readonly source: { cloudId: string; cameraId: string },
    origin: Vector3,
  ) {
    this.source = { cloudId: source.cloudId, cameraId: source.cameraId };
    this.origin = origin.clone();
    this.mask = new PipeSelectionMask(origin);
  }

  /**
   * @param update Accepted tracker update.
   * @returns False without publishing a truncated update if the mask budget is exhausted.
   */
  add(update: PipeTraceUpdate): boolean {
    if (!this.mask.add(update)) return false;
    const destination = update.kind === "gap" ? this.inferred : this.observed;
    destination.push(
      ...update.from.clone().sub(this.origin).toArray(),
      ...update.step.frontier.center.clone().sub(this.origin).toArray(),
    );
    this.length += update.from.distanceTo(update.step.frontier.center);
    this.inferredLength += update.inferredLength;
    return true;
  }

  /**
   * @param result Final frontier outcomes, when available.
   * @returns Independent geometry and membership for render/report consumers.
   */
  snapshot(result?: PipeTraceResult): PipeRunSelection {
    return {
      kind: "pipe-run",
      id: this.id,
      ...this.source,
      origin: this.origin.clone(),
      mask: this.mask.snapshot(),
      centerline: new Float32Array(this.observed),
      inferredCenterline: new Float32Array(this.inferred),
      length: this.length,
      inferredLength: this.inferredLength,
      coverage: result ? "partial" : "growing",
      result: result
        ? {
            ...result,
            ends: result.ends.map((end) => ({
              ...end,
              frontier: {
                ...end.frontier,
                center: end.frontier.center.clone(),
                axis: end.frontier.axis.clone(),
              },
              candidates: end.candidates.map((candidate) => ({
                ...candidate,
                frontier: {
                  ...candidate.frontier,
                  center: candidate.frontier.center.clone(),
                  axis: candidate.frontier.axis.clone(),
                },
                cylinder: {
                  ...candidate.cylinder,
                  center: candidate.cylinder.center.clone(),
                  axis: candidate.cylinder.axis.clone(),
                  radial: candidate.cylinder.radial.clone(),
                },
                samples: candidate.samples.map((sample) => ({
                  ...sample,
                  point: sample.point.clone(),
                  normal: sample.normal.clone(),
                })),
              })),
            })),
          }
        : undefined,
    };
  }
}

/**
 * @param cloud Source tree and shared loader, owned by the viewer.
 * @param seed Accepted analytic seed used only to start the independent run.
 * @param options Lifetime, worker solver and immutable progress publication.
 * @returns A pipe-run result whose length and geometry match admitted observed support.
 */
export async function tracePipeSelection(
  cloud: Parameters<typeof traceLodPipeRun>[0],
  seed: PipeRunSeed,
  options: Omit<Parameters<typeof traceLodPipeRun>[2], "accept" | "publish"> & {
    publish?(selection: PipeRunSelection): void;
  },
): Promise<PipeRunSelection> {
  if (!seed.cylinder) {
    throw new Error("Pipe run requires an accepted cylinder seed");
  }
  const builder = new PipeRunSelectionBuilder(seed, seed.origin);
  if (!options.signal.aborted) options.publish?.(builder.snapshot());
  const cylinder = { ...seed.cylinder, center: seed.cylinder.center.clone() };
  cylinder.center.addScaledVector(
    cylinder.axis,
    seed.origin.clone().sub(cylinder.center).dot(cylinder.axis),
  );
  const result = await traceLodPipeRun(cloud, cylinder, {
    ...options,
    accept: (update) => builder.add(update),
    publish: () => options.publish?.(builder.snapshot()),
  });
  const selection = builder.snapshot(result);
  if (!options.signal.aborted) options.publish?.(selection);
  return selection;
}
